import React, { createContext, useContext, useState, useEffect } from 'react';

const ThreadsAuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

export const ThreadsAuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [isLoading, setIsLoading] = useState(true); // true until token check completes

    // On mount: read the stored token and validate it with the server.
    // If the server says it's invalid/expired, wipe localStorage so the
    // "Login with Threads" button re-appears (fixes the mobile stale-token trap).
    useEffect(() => {
        const storedToken = localStorage.getItem('threads_token');
        const storedUser = localStorage.getItem('threads_user');

        if (!storedToken || !storedUser) {
            setIsLoading(false);
            return;
        }

        fetch(`${BACKEND_URL}/auth/threads/me`, {
            headers: { Authorization: `Bearer ${storedToken}` },
        })
            .then(res => {
                if (res.ok) {
                    // Token is still valid — restore the session
                    setToken(storedToken);
                    setUser(JSON.parse(storedUser));
                } else {
                    // Token expired or invalid — clear it so the login button re-appears
                    localStorage.removeItem('threads_token');
                    localStorage.removeItem('threads_user');
                }
            })
            .catch(() => {
                // Network error (backend unreachable) — keep the stored session rather
                // than logging the user out, so offline reloads don't break things.
                setToken(storedToken);
                try { setUser(JSON.parse(storedUser)); } catch { /* ignore */ }
            })
            .finally(() => setIsLoading(false));
    }, []);

    const login = (newToken, newUser) => {
        localStorage.setItem('threads_token', newToken);
        localStorage.setItem('threads_user', JSON.stringify(newUser));
        setToken(newToken);
        setUser(newUser);
    };

    const logout = () => {
        localStorage.removeItem('threads_token');
        localStorage.removeItem('threads_user');
        setToken(null);
        setUser(null);
    };

    return (
        <ThreadsAuthContext.Provider
            value={{ user, token, isLoggedIn: !!token, isLoading, login, logout }}
        >
            {children}
        </ThreadsAuthContext.Provider>
    );
};

export const useThreadsAuth = () => {
    const ctx = useContext(ThreadsAuthContext);
    if (!ctx) throw new Error('useThreadsAuth must be used within ThreadsAuthProvider');
    return ctx;
};

export default ThreadsAuthContext;
